import jsPDF from "jspdf";
import { loadLogo, drawLogo } from "./loadLogo";
import { chapters, costCategories } from "../data/chapters";

export async function generatePdf(completedSteps, costValues, userName) {
  const mark = await loadLogo("/logo-mark.png");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const addPageIfNeeded = (needed) => {
    if (y + needed > 270) {
      doc.addPage();
      y = margin;
    }
  };

  // Header
  doc.setFillColor(30, 58, 138); // #1E3A8A
  doc.rect(0, 0, pageWidth, 40, "F");
  // Logo badge (white rounded box) on the right of the header
  if (mark) {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(pageWidth - margin - 30, 5, 30, 30, 3, 3, "F");
    drawLogo(doc, mark, pageWidth - margin - 15, 8, 24, "center");
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text("Texas LLC Formation Guide", margin, 18);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("Your Personalized Progress Report", margin, 28);
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}${userName ? ` | ${userName}` : ""}`, margin, 35);

  y = 50;

  // Build checklist index mapping
  let globalIndex = 0;
  const chapterChecklistStart = chapters.map((ch) => {
    const start = globalIndex;
    globalIndex += ch.checklistItems.length;
    return start;
  });
  const totalChecklist = chapters.reduce((a, c) => a + c.checklistItems.length, 0);
  const completedTotal = completedSteps.length;
  const progressPercent = totalChecklist > 0 ? Math.round((completedTotal / totalChecklist) * 100) : 0;

  // Overall Progress
  doc.setTextColor(30, 25, 24);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Overall Progress", margin, y);
  y += 7;

  // Progress bar
  doc.setDrawColor(30, 25, 24);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, y, contentWidth, 6, 3, 3, "S");
  if (progressPercent > 0) {
    doc.setFillColor(30, 58, 138);
    const barWidth = Math.max(3, (contentWidth * progressPercent) / 100);
    doc.roundedRect(margin, y, barWidth, 6, 3, 3, "F");
  }
  y += 10;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`${progressPercent}% complete — ${completedTotal} of ${totalChecklist} tasks done`, margin, y);
  y += 12;

  // Separator
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // Chapters with checklists
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Chapter Checklist", margin, y);
  y += 8;

  chapters.forEach((ch, i) => {
    const startIdx = chapterChecklistStart[i];
    const chCompleted = ch.checklistItems.filter((_, j) => completedSteps.includes(startIdx + j)).length;
    const chTotal = ch.checklistItems.length;
    const allDone = chCompleted === chTotal;

    addPageIfNeeded(10 + chTotal * 6);

    // Chapter title
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 58, 138);
    const status = allDone ? " [COMPLETE]" : ` (${chCompleted}/${chTotal})`;
    doc.text(`Step ${ch.id}: ${ch.title}${status}`, margin, y);
    y += 6;

    // Checklist items
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    ch.checklistItems.forEach((item, ci) => {
      const idx = startIdx + ci;
      const checked = completedSteps.includes(idx);
      addPageIfNeeded(6);
      const checkbox = checked ? "[x]" : "[ ]";
      doc.text(`  ${checkbox}  ${item}`, margin + 2, y);
      y += 5.5;
    });
    y += 4;
  });

  // Cost Estimator Section
  addPageIfNeeded(60);
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 25, 24);
  doc.text("Startup Cost Estimate", margin, y);
  y += 8;

  let total = 0;
  costCategories.forEach((cat) => {
    addPageIfNeeded(8);
    const val = costValues[cat.key] ?? cat.min;
    total += Number(val) || 0;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text(cat.label, margin, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 25, 24);
    doc.text(`$${val}`, pageWidth - margin, y, { align: "right" });
    y += 6;
  });

  y += 2;
  doc.setDrawColor(30, 58, 138);
  doc.setLineWidth(0.8);
  doc.line(margin, y, pageWidth - margin, y);
  y += 7;

  doc.setFillColor(30, 58, 138);
  doc.roundedRect(margin, y - 3, contentWidth, 12, 2, 2, "F");
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Estimated Total", margin + 5, y + 5);
  doc.text(`$${total}`, pageWidth - margin - 5, y + 5, { align: "right" });

  // Footer
  y += 20;
  addPageIfNeeded(10);
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.text("Texas LLC Formation Guide — Generated with your personalized progress data", pageWidth / 2, y, { align: "center" });

  doc.save("Texas_LLC_Progress_Report.pdf");
}
